import { useCallback, useState } from "react";
import { generatePath } from "react-router";

import { cn } from "@/lib/chillnn/cn";
import dayjs from "@/lib/dayjs";
import { SalesMastType } from "@/lib/gql/__generated__/graphql";

import { PATHS } from "@/routes/paths";

import ArrowRightAlt from "@/components/ui/icon/arrow-right-alt";
import { TableCell, TableRow } from "@/components/ui/table/table";

import { SearchResultTableTBodyPopover } from "./popover";

import type {
	SearchResultTableRowData,
	SearchResultTableRowDataECType,
	SearchResultTableRowDataReservationCreditType,
} from "../../../../model/type/view-data";

type Props = {
	row: SearchResultTableRowData;
};

const FORMAT_DATE_TEMPLATE = "YYYY年MM月DD日";

const isReservationType = (row: SearchResultTableRowData): row is SearchResultTableRowDataReservationCreditType =>
	row.salesMastType === SalesMastType.ReservationCredit || row.salesMastType === SalesMastType.ReservationLocal;

const isEcType = (row: SearchResultTableRowData): row is SearchResultTableRowDataECType =>
	row.salesMastType === SalesMastType.Ec;

export const SearchResultTableTBody = ({ row }: Props) => {
	const {
		buyerName,
		purchaseItemName,

		income,

		transferredAt,

		isPlus,
	} = row;

	const incomeText = `${income.toLocaleString()}円`;

	const transferredAtText = dayjs(transferredAt).format(FORMAT_DATE_TEMPLATE);

	const [modalOpen, setModalOpen] = useState(false);

	const handleRowClick = useCallback(() => {
		setModalOpen(true);
	}, []);

	const reservationDetailHref = isReservationType(row)
		? generatePath(PATHS.HOTEL.MANAGEMENT.RESERVATION.DETAIL, {
				hotelID: row.hotelID,
				reservationID: row.reservationID,
			})
		: undefined;

	const ecDetailHref = isEcType(row)
		? generatePath(PATHS.HOTEL.MANAGEMENT.EC_SALES.DETAIL, {
				hotelID: row.hotelID,
				ecID: row.userECReceiptID,
			})
		: undefined;

	return (
		<TableRow className="cursor-pointer" onClick={handleRowClick}>
			<TableCell className="text-center">
				<ArrowRightAlt className={cn(isPlus ? "text-blue-400" : "rotate-180 text-brand")} />
			</TableCell>
			<TableCell className="text-center">{incomeText}</TableCell>
			<TableCell className="text-center">{transferredAtText}</TableCell>
			<TableCell className="text-center">{buyerName}</TableCell>
			<TableCell className="text-center">{purchaseItemName}</TableCell>
			<TableCell className="text-center">
				{isReservationType(row) && (
					<a
						href={reservationDetailHref}
						target="_blank"
						rel="noopener noreferrer"
						className="hover:text-blue-800 text-sm text-blue-600 underline"
						onClick={(e) => e.stopPropagation()}
					>
						{row.reservationID}
					</a>
				)}
				{isEcType(row) && (
					<a
						href={ecDetailHref}
						target="_blank"
						rel="noopener noreferrer"
						className="hover:text-blue-800 text-sm text-blue-600 underline"
						onClick={(e) => e.stopPropagation()}
					>
						{row.userECReceiptID}
					</a>
				)}
			</TableCell>
			<TableCell>
				<SearchResultTableTBodyPopover row={row} open={modalOpen} onOpenChange={setModalOpen} />
			</TableCell>
		</TableRow>
	);
};
